import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  FolderKanban,
  Plus,
  Trash2,
  Calendar
} from "lucide-react";
import { toast } from "sonner";
import { API } from "../../App";

const STATUS_LABELS = {
  brouillon: { label: "Brouillon", className: "bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300" },
  cadrage: { label: "En cadrage", className: "bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300" },
  actif: { label: "Actif", className: "bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300" },
  termine: { label: "Terminé", className: "bg-slate-300 dark:bg-slate-600 text-slate-800 dark:text-slate-200" },
};

const TYPE_LABELS = {
  transformation: "Transformation",
  creation: "Création",
  innovation: "Innovation",
  restructuration: "Restructuration",
  evenementiel: "Événementiel",
  autre: "Autre",
};

const ProjectsListPage = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await fetch(`${API}/projects`, { credentials: "include" });
      if (response.ok) {
        setProjects(await response.json());
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleDelete = async (e, projectId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Supprimer ce projet définitivement ?")) return;

    try {
      const response = await fetch(`${API}/projects/${projectId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (response.ok) {
        setProjects((prev) => prev.filter((p) => p.project_id !== projectId));
        toast.success("Projet supprimé");
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="projects-list-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: "Outfit" }}>
            Vos projets
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {projects.length === 0
              ? "Créez votre premier projet structuré."
              : `${projects.length} projet${projects.length > 1 ? "s" : ""} en cours.`}
          </p>
        </div>
        <Button
          onClick={() => navigate("/dashboard/projects/new")}
          className="bg-blue-600 hover:bg-blue-700 text-white glow-blue"
          data-testid="new-project-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau projet
        </Button>
      </div>

      {projects.length === 0 ? (
        <Card className="border-dashed border-2 bg-transparent">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-14 h-14 rounded-full bg-blue-100 dark:bg-blue-500/10 flex items-center justify-center mb-4">
              <FolderKanban className="w-7 h-7 text-blue-600 dark:text-blue-400" />
            </div>
            <p className="text-lg font-semibold text-slate-900 dark:text-white">
              Aucun projet pour l'instant.
            </p>
            <p className="text-slate-500 dark:text-slate-400 mt-1 max-w-sm">
              Décrivez votre idée en quelques minutes et obtenez vos 13 livrables structurés.
            </p>
            <Button
              onClick={() => navigate("/dashboard/projects/new")}
              className="mt-6 bg-blue-600 hover:bg-blue-700 text-white"
            >
              Créer mon premier projet
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((project) => {
            const status = STATUS_LABELS[project.status] || STATUS_LABELS.brouillon;
            return (
              <Link key={project.project_id} to={`/dashboard/projects/${project.project_id}`}>
                <Card className="hover:shadow-md transition-all hover:-translate-y-0.5 h-full">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <Badge className={status.className}>{status.label}</Badge>
                      <button
                        onClick={(e) => handleDelete(e, project.project_id)}
                        className="text-slate-400 hover:text-red-500 transition-colors"
                        aria-label="Supprimer le projet"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    <h3 className="mt-4 font-semibold text-lg text-slate-900 dark:text-white" style={{ fontFamily: "Outfit" }}>
                      {project.brief?.name || "Projet sans nom"}
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      {TYPE_LABELS[project.brief?.type] || "Projet"} — {project.brief?.sector || "secteur non précisé"}
                    </p>

                    <div className="flex items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500 mt-4">
                      <Calendar className="w-3.5 h-3.5" />
                      Mis à jour le{" "}
                      {new Date(project.updated_at).toLocaleDateString("fr-FR", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProjectsListPage;